/**
 * GlossaryDrawer — the dashboard's authored glossary (DSA 1.16,
 * prototype v7 "📖 Glosario": what exactly each number means). A button
 * opens a right drawer with the sections; every text is bilingual and
 * comes from `dashboard_config.glossary` — the render owns no
 * vocabulary. Esc and the backdrop close it.
 */
import { BookOpen, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { resolveLocalized, type DisplayLabel } from "./i18n-resolve";
import { useLang } from "./i18n-binding";
import type { DashboardGlossary } from "./types";

export function GlossaryDrawer({ glossary }: { glossary: DashboardGlossary }): JSX.Element {
  const { t } = useTranslation();
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const r = (v: DisplayLabel | null | undefined): string => resolveLocalized(v as DisplayLabel, lang);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const title = r(glossary.title) || t("dashboard.glossary.title");
  return (
    <>
      <button
        type="button"
        data-glossary-open=""
        onClick={() => setOpen(true)}
        className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-neutral-200 bg-white px-[9px] py-1 text-2xs font-semibold text-neutral-700 hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <BookOpen size={13} aria-hidden="true" />
        {t("dashboard.glossary.open")}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            data-glossary-backdrop=""
            className="absolute inset-0 bg-neutral-900/30"
            onClick={() => setOpen(false)}
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label={title}
            data-glossary-drawer=""
            className="relative flex h-full w-full flex-col bg-white shadow-xl md:w-[560px]"
          >
            <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-5 py-4">
              <div className="min-w-0">
                <h2 className="text-base font-bold text-neutral-900">{title}</h2>
                {glossary.subtitle && (
                  <p className="mt-0.5 text-xs text-neutral-600">{r(glossary.subtitle)}</p>
                )}
              </div>
              <button
                type="button"
                aria-label={t("dashboard.glossary.close")}
                onClick={() => setOpen(false)}
                className="rounded-md p-1 text-neutral-600 hover:bg-neutral-100"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
              {glossary.sections.map((sec, si) => (
                <section key={si} data-glossary-section="">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-neutral-600">
                    {r(sec.title)}
                  </h3>
                  {sec.intro && <p className="mt-1 text-sm text-neutral-700">{r(sec.intro)}</p>}
                  <dl className="mt-2 space-y-2">
                    {sec.entries.map((ent, ei) => (
                      <div key={ei} className="rounded-lg border border-neutral-200 px-3 py-2">
                        <dt className="text-sm font-bold text-neutral-900">{r(ent.term)}</dt>
                        {ent.formula && (
                          <dd className="mt-1 rounded-md bg-neutral-50 px-2 py-1 font-mono text-xs text-neutral-800">
                            {r(ent.formula)}
                          </dd>
                        )}
                        <dd className="mt-1 text-sm text-neutral-700">{r(ent.definition)}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ))}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
